<template>
  <header class="pdf-header">
    <div class="pdf-header__brand">
      <img
        class="pdf-header__buzz-logo"
        :src="require('@/assets/buzzztv_logo.png')"
        alt="buzzztv-logo"
        @load="onBuzzAssetDone"
        @error="onBuzzAssetDone"
      />
      <div class="pdf-header__dates">{{ time.timeFrom }} - {{ time.timeTo }}</div>
    </div>
    <div class="pdf-header__body">
      <div class="pdf-header__logos" v-if="currCompany && currCompany.logo">
        <SVGIcon
          v-if="currCompany.logo.split('.').pop() === 'svg'"
          :svg="currCompany.logo"
          @doneRender="onCompanyAssetDone"
          class="pdf-header__company-logo"
        />
        <img
          v-else-if="currCompany.logo"
          class="pdf-header__company-logo"
          @load="onCompanyAssetDone"
          @error="onCompanyAssetDone"
          :src="currCompany.logo"
          alt=""
        />
      </div>
      <div class="pdf-header__text-block">
        <div class="pdf-header__title">{{ documentTitleText }}</div>
        <div class="pdf-header__line" v-if="currCompany">{{ currCompany.name }} | {{ currSite.name }}</div>
        <div class="pdf-header__line pdf-header__line--minor" v-if="includeRoomLine && currRoom">{{ currRoom.name }}</div>
        <div class="pdf-header__line pdf-header__line--minor" v-if="includeShiftsLine && filteredShifts.length > 0">
          {{ $t('shifts') }}: {{ filteredShifts.map((shift) => shift.name).join(', ') }}
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import SVGIcon from './SVGIcon.vue'

export default {
  name: 'pdf-header',
  components: { SVGIcon },
  props: {
    currCompany: { type: Object, required: true },
    currSite: { type: Object, required: true },
    currRoom: { type: [Object], required: false, default: null },
    time: { type: Object, required: true },
    /** Vue i18n key for the main PDF title; falls back to $route.name when empty */
    titleI18nKey: { type: String, default: '' },
    /** When false, shifts line is never shown (e.g. room cleaner PDF) */
    includeShiftsLine: { type: Boolean, default: true },
    /** When false, room name line is hidden (e.g. site analytics PDF) */
    includeRoomLine: { type: Boolean, default: true },
    filteredShifts: { type: Array, default: () => [] },
  },
  data() {
    return {
      buzzReady: false,
      companyReady: false,
      readyEmitted: false,
      fallbackTimer: null,
    }
  },
  computed: {
    documentTitleText() {
      if (this.titleI18nKey) {
        return this.$t(this.titleI18nKey);
      }
      return this.$t(this.$route.name);
    },
    needsCompanyAsset() {
      return !!(this.currCompany && this.currCompany.logo);
    },
  },
  mounted() {
    this.fallbackTimer = setTimeout(() => this.emitReadyOnce(), 5000);
    this.$nextTick(() => {
      const img = this.$el && this.$el.querySelector('.pdf-header__buzz-logo');
      if (img && img.complete && img.naturalHeight > 0) {
        this.onBuzzAssetDone();
      }
    });
  },
  beforeDestroy() {
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  },
  methods: {
    onBuzzAssetDone() {
      if (this.buzzReady) return;
      this.buzzReady = true;
      this.tryEmitReady();
    },
    onCompanyAssetDone() {
      if (this.companyReady) return;
      this.companyReady = true;
      this.tryEmitReady();
    },
    tryEmitReady() {
      if (this.readyEmitted) return;
      if (!this.buzzReady) return;
      if (this.needsCompanyAsset && !this.companyReady) return;
      this.emitReadyOnce();
    },
    emitReadyOnce() {
      if (this.readyEmitted) return;
      this.readyEmitted = true;
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.$emit('ready');
    },
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

/* Compact header for html2pdf: must stay within page width/height (no 150% width, no oversized type) */
.pdf-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 0;
  height: auto;
  padding: 0.35rem 0.5rem;
  background-color: #fff;
  flex-wrap: nowrap;

  @media print {
    break-inside: avoid;
  }
}

.pdf-header__brand {
  @include flex-center;
  flex: 0 0 auto;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  max-width: 28%;
  min-width: 0;
}

.pdf-header__buzz-logo {
  height: 3.25rem;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.pdf-header__dates {
  font: 600 0.7rem/1.25 Assistant, system-ui, sans-serif;
  text-align: center;
  color: #2b2f35;
  word-break: break-word;
}

.pdf-header__body {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 72%;
}

.pdf-header__logos {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 38%;
  min-width: 0;
}

.pdf-header__company-logo {
  display: block;
  max-width: 7.5rem;
  max-height: 2.75rem;
  width: auto;
  height: auto;
  object-fit: contain;
}

.pdf-header__text-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 0.12rem;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;

  @include rtl {
    align-items: flex-start;
    text-align: left;
  }
}

.pdf-header__title {
  font: 700 1.1rem/1.2 Assistant, system-ui, sans-serif;
  text-transform: capitalize;
  letter-spacing: -0.02em;
  color: #2b2f35;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.pdf-header__line {
  font: 600 0.7rem/1.25 Assistant, system-ui, sans-serif;
  color: #2b2f35;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.pdf-header__line--minor {
  font-weight: 500;
  font-size: 0.65rem;
  color: #3d4450;
}
</style>
